/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ApiQuery } from '@app/core/http/api-query';
import { Observable, of, Subject } from 'rxjs';
import { Industry } from '@app/settings/industries/industries.model';
import { IndustriesService } from '@app/settings/industries/industries.service';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { IndustriesFormService } from '@app/settings/industries/industries-form.service';
import { Logger } from '@app/core';

const log = new Logger('IndustrySelectComponent');

@Component({
  selector: 'app-industry-select',
  templateUrl: './industry-select.component.html',
  styleUrls: ['./industry-select.component.scss'],
})
export class IndustrySelectComponent implements OnInit {
  @Input() parentFormGroup: FormGroup;
  loader: boolean;
  apiQuery: ApiQuery = new ApiQuery();
  data: Observable<Industry[]>;
  input$ = new Subject<string>();
  @Output() select = new EventEmitter();
  @Input() onlyActive = true;
  @Input() isInModal = true;

  constructor(private industryService: IndustriesService, private industryFormService: IndustriesFormService) {}

  ngOnInit() {
    const preSelectedIndustry = this.parentFormGroup.get('industry');
    const preSelectedIndustryId = this.parentFormGroup.get('industry_id');

    /**
     * Попълва избрания industry на едит.
     * Ако има FormControl 'industry' в parentFormGroup.
     * Ако няма ще се опита да го get-не по ID
     */
    if (preSelectedIndustry) {
      /*
       * Следи за промени в industry FormControl-a и когато намери валиден Industry спира да следи
       */
      const preselectedIndustrySubscriber = preSelectedIndustry.valueChanges.subscribe((industry: any) => {
        if (industry && industry.name) {
          this.data = of([industry]);
          preselectedIndustrySubscriber.unsubscribe();
        }
      });
    } else if (preSelectedIndustryId) {
      /*
       * Ако не е намерен industry FormControl-a търси industry_id FormControl и се опитва да го get-не по ID
       */
      const preselectedIndustryIdSubscriber = preSelectedIndustryId.valueChanges.subscribe((industryId: any) => {
        if (industryId) {
          this.loadById(industryId);
          preselectedIndustryIdSubscriber.unsubscribe();
        }
      });
    }

    this.industrySearch();
  }

  // loadVenues(keyword?: string) {
  //   this.loader = true;
  //
  //   this.industryService.index(this.apiQuery.addFilter('name', keyword)).subscribe((response: PaginatedResponse) => {
  //     this.data = of(response.data);
  //     this.loader = false;
  //   });
  // }

  loadById(id: number) {
    this.loader = true;
    this.industryService.show(id).subscribe((industry: Industry) => {
      this.data = of([industry]);
      this.loader = false;
    });
  }

  industrySearch() {
    if (this.onlyActive) {
      this.apiQuery.addFilter('active', true);
    }

    this.input$
      .pipe(
        tap(() => (this.loader = true)),
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((term) =>
          this.industryService.index(this.apiQuery.addFilters({ name: term })).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.loader = false))
          )
        )
      )
      .subscribe((industries: PaginatedResponse) => {
        this.data = of(industries.data);
        this.loader = false;
      });
  }

  add(searchTerm: string) {
    this.industryFormService.open(new Industry().deserialize({ name: searchTerm })).then(
      (response) => {
        this.loadById(response.id);
        this.parentFormGroup.get('industry_id').setValue(response.id);
      },
      (err) => {
        log.error(err);
      }
    );
  }

  onChange(industry: any) {
    this.select.emit(industry);
  }

  setData(data: Industry) {
    if (data) {
      this.data = of([data]);
    } else {
      this.data = of([]);
    }
  }
}
